import { MediaDropPill } from '@arthelper/ui';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient, useMutation, useQuery, type QueryOptions } from '@apollo/client';
import { Button, Popover, PopoverContent, PopoverTrigger, Textarea } from '@artstorefronts/ui';
import { format } from 'date-fns';
import { MessagesSquare, SendHorizontal, Smile } from 'lucide-react';
import { EmojiPickerPopover } from './EmojiPickerPopover';
import { imagesFromClipboard } from './DmAttachments';
import { MessagePartImages } from './MessagePartImages';
import { DmQuickReplies } from './DmQuickReplies';
import { useDmAttachments } from '~/hooks/useDmAttachments';
import { QUICK_REPLY_PHRASES } from '@arthelper/shared/dm-quick-replies';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  DOCK_LIST_MESSAGES_VARIABLES,
  MESSAGE_QUERY,
  MESSAGES_QUERY,
  MY_PROFILE_QUERY,
  SOCIAL_BADGE_COUNTS_QUERY,
} from '~/graphql/queries';
import { INBOX_MESSAGES_REFETCH_WRITE_POLICY, refetchInboxMessages } from '@arthelper/shared/inbox-messages-cache';
import {
  DM_SUGGESTED_REPLIES_QUERY,
  MARK_MESSAGE_READ_MUTATION,
  SEND_MESSAGE_PART_MUTATION,
} from '~/graphql/direct-messages';
import { TranslatableMessageText } from '~/components/messaging/TranslatableMessageText';
import { getSenderDisplayName } from '~/utils/participantHelpers';
import { UserAvatar } from '~/components/UserAvatar';
import { DisabledUserName } from '~/components/DisabledUserName';
import { getDisabledUserName } from '~/components/DisabledUserName';
import { useAnalytics } from '~/context/analytics';
import { SpamWarningBanner } from './SpamWarningBanner';
import { useMarkThreadReadOnOpen } from '~/hooks/useMarkThreadReadOnOpen';
import { applyMarkMessageReadCacheUpdate } from '~/utils/markMessageReadCache';

const MAX_CONTENT_LENGTH = 5000;

interface Props {
  threadId: string;
  isVisible?: boolean;
}

/**
 * Compact thread transcript + reply composer for use inside the messaging
 * dock. Mirrors `MessageDetail.tsx` but without the external app chrome
 * and back-to-list navigation (the dock shell owns the header).
 */
export function DockThreadView({ threadId, isVisible = true }: Props) {
  const { t } = useTranslation('social');
  const {
    amplitude: { track },
  } = useAnalytics();

  const { data, loading, error } = useQuery(MESSAGE_QUERY, {
    variables: { id: threadId },
    fetchPolicy: 'cache-and-network',
    pollInterval: 60000,
  });

  const myProfile = useQuery(MY_PROFILE_QUERY, { fetchPolicy: 'cache-first' });
  const viewerAsfId: string | undefined = myProfile.data?.profile?.user?.asfId?.toString();

  const [markMessageRead] = useMutation(MARK_MESSAGE_READ_MUTATION, {
    refetchQueries: [
      { query: SOCIAL_BADGE_COUNTS_QUERY },
      // Preview and list share one cache entry. Refetch the longer page only, and
      // merge: this descriptor is a one-off query, so the watch's policy does not apply.
      {
        query: MESSAGES_QUERY,
        variables: DOCK_LIST_MESSAGES_VARIABLES,
        refetchWritePolicy: INBOX_MESSAGES_REFETCH_WRITE_POLICY,
      } as QueryOptions,
    ],
    // Zero unreadCount only after a true API response so a silent false does
    // not hide stale unread state or suppress mark-read retries.
    update: (cache, { data }, { variables }) => {
      applyMarkMessageReadCacheUpdate(cache, data, variables?.messageId);
    },
    onError: (err) => {
      console.error('markMessageRead failed', err);
    },
  });

  const [sendMessagePart, { loading: sending }] = useMutation(SEND_MESSAGE_PART_MUTATION, {
    refetchQueries: [
      { query: MESSAGE_QUERY, variables: { id: threadId } },
      { query: SOCIAL_BADGE_COUNTS_QUERY },
      // Chips and the collapsed preview read this same cache entry. The 50-row
      // refetch reorders the head and keeps the paged tail; a 20-row refetch would
      // shift that tail. Merge because this descriptor is a one-off query.
      {
        query: MESSAGES_QUERY,
        variables: DOCK_LIST_MESSAGES_VARIABLES,
        refetchWritePolicy: INBOX_MESSAGES_REFETCH_WRITE_POLICY,
      } as QueryOptions,
    ],
    awaitRefetchQueries: true,
  });

  const [reply, setReply] = useState('');
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const replyRef = useRef<HTMLTextAreaElement | null>(null);
  const { attachments, addFiles, clear, uploading, imageIds, fileAttachments, dropProps } = useDmAttachments();
  const dropScope = useRef<HTMLFormElement>(null);

  const client = useApolloClient();
  const message = data?.message;
  const messageMatchesThread = !!message && message.id === threadId;
  const messageParts = useMemo(() => message?.parts?.items ?? [], [message]);
  const isInAppThread = message?.channel?.toLowerCase() === 'in_app';

  // Quick replies answer someone else, so they only exist while the newest message is
  // theirs. Keyed on that part: one AI call per inbound message, none after your own
  // reply (curated set shows instantly; the AI set swaps in when it arrives).
  const lastPart: any = messageParts[messageParts.length - 1];
  const lastSender = lastPart ? (lastPart.sender?.user?.asfId?.toString() ?? lastPart.sender?.userId) : null;
  const awaitingReply = Boolean(lastPart) && viewerAsfId != null && lastSender !== String(viewerAsfId);
  const { data: aiReplyData } = useQuery(DM_SUGGESTED_REPLIES_QUERY, {
    variables: { messageId: threadId, partId: lastPart?.id },
    skip: !messageMatchesThread || !isInAppThread || !awaitingReply,
    fetchPolicy: 'cache-first',
  });
  const aiPhrases: string[] = aiReplyData?.dmSuggestedReplies ?? [];
  const quickPhrases = aiPhrases.length > 0 ? aiPhrases : QUICK_REPLY_PHRASES;

  // When the thread poll detects new parts, immediately sync the inbox list
  // so MESSAGES_QUERY doesn't have to wait for its own independent 60s timer.
  // The ref is keyed on threadId so navigating to a longer thread (or reading
  // stale cache from a previous thread) never triggers a spurious refetch.
  const prevPartsLengthRef = useRef<{ threadId: string; length: number } | null>(null);
  useEffect(() => {
    if (!messageMatchesThread) return;
    const prev = prevPartsLengthRef.current;
    prevPartsLengthRef.current = { threadId, length: messageParts.length };
    if (prev === null || prev.threadId !== threadId || messageParts.length <= prev.length) return;
    refetchInboxMessages(client, MESSAGES_QUERY);
  }, [threadId, messageMatchesThread, messageParts.length, client]);

  const messageLoaded = messageMatchesThread && isVisible;
  const unreadCount: number = messageMatchesThread ? (message?.unreadCount ?? 0) : 0;
  useMarkThreadReadOnOpen(threadId, messageLoaded, unreadCount, markMessageRead);

  useEffect(() => {
    if (!isVisible) return;
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messageParts.length, isVisible]);

  useEffect(() => {
    if (!isInAppThread || !isVisible) return;
    replyRef.current?.focus();
  }, [threadId, isInAppThread, isVisible]);

  if ((loading && !message) || (threadId && message && !messageMatchesThread)) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900" />
      </div>
    );
  }

  if (error || !message) {
    return (
      <div className="flex-1 flex items-center justify-center px-4">
        <p className="text-sm text-gray-500 text-center">{t('messages.failed-to-load-detail')}</p>
      </div>
    );
  }

  const isInApp = message.channel?.toLowerCase() === 'in_app';
  // `otherParticipant` kept here for future per-part sender-matching
  // (e.g. avatar alongside each incoming bubble). The dock shell renders
  // the header name/avatar itself via MESSAGE_QUERY.
  void message.participants;

  const trimmed = reply.trim();
  const canSend =
    isInApp &&
    !sending &&
    !uploading &&
    trimmed.length <= MAX_CONTENT_LENGTH &&
    (trimmed.length > 0 || imageIds.length > 0 || fileAttachments.length > 0);

  async function sendContent(
    content: string,
    ids: string[],
    files: typeof fileAttachments,
    source: 'reply' | 'quick_reply'
  ): Promise<boolean> {
    try {
      const { data: result } = await sendMessagePart({
        variables: { input: { messageId: threadId, content, imageIds: ids, files } },
      });
      const payload = result?.sendMessagePart;
      if (!payload?.success) {
        const err = payload?.error || '';
        if (err === 'NO_CONNECTIONS_IN_THREAD') {
          toast.error(t('messages.reply-error-not-connected'));
        } else {
          toast.error(err || t('messages.reply-error-generic'));
        }
        track('dm_thread_message_failed', { source: 'dock', thread_id: threadId });
        return false;
      }
      track('dm_thread_message_sent', {
        source: 'dock',
        thread_id: threadId,
        content_length: content.length,
        image_count: ids.length,
        via: source,
      });
      return true;
    } catch (err: any) {
      toast.error(err?.message || t('messages.reply-error-generic'));
      track('dm_thread_message_failed', { source: 'dock', thread_id: threadId });
      return false;
    }
  }

  async function handleSend(e?: React.FormEvent) {
    e?.preventDefault();
    if (!canSend) return;
    const ok = await sendContent(trimmed, imageIds, fileAttachments, 'reply');
    if (ok) {
      setReply('');
      clear();
    }
  }

  async function sendQuickReply(text: string) {
    if (!isInApp || sending || uploading) return;
    await sendContent(text, [], [], 'quick_reply');
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey && !e.altKey) {
      e.preventDefault();
      void handleSend();
    }
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
        {messageParts.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <MessagesSquare className="size-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">{t('messages.not-started-yet')}</p>
            </div>
          </div>
        ) : (
          messageParts.map((mp: any, i: number) => {
            const senderUserId = mp.sender.user?.asfId?.toString() ?? mp.sender.userId;
            const isSelf = isInApp
              ? viewerAsfId != null && senderUserId === viewerAsfId
              : mp.sender.kind?.toLowerCase() === 'user';
            const senderIsDisabled = !!mp.sender.user?.profile?.isDisabled;
            const senderName = isSelf
              ? t('messages.you-label')
              : senderIsDisabled
                ? getDisabledUserName()
                : getSenderDisplayName(mp.sender);
            const avatarUrl = mp.sender.user?.profile?.avatarUrl ?? null;
            const firstName = mp.sender.user?.firstName;
            const lastName = mp.sender.user?.lastName;
            const displayName = mp.sender.user?.profile?.displayName;
            const isHumanVerified = !!mp.sender.user?.profile?.isHumanVerified;

            // Collapse consecutive bubbles from the same sender: only the
            // first shows the avatar + name header, the rest stack below.
            const prev = messageParts[i - 1];
            const prevSameSender = prev && (prev.sender.user?.asfId?.toString() ?? prev.sender.userId) === senderUserId;
            const showHeader = !prevSameSender;

            return (
              <React.Fragment key={mp.id}>
                {/* Spam banner — visible to recipients only */}
                {mp.status === 'SPAM' && !isSelf && <SpamWarningBanner messagePartId={mp.id} />}
                <div className={`flex gap-2 ${isSelf ? 'flex-row-reverse' : 'flex-row'}`}>
                  <div className="w-7 flex-shrink-0 flex items-end">
                    {!isSelf && showHeader && (
                      <UserAvatar
                        avatarUrl={avatarUrl}
                        firstName={firstName}
                        lastName={lastName}
                        displayName={displayName}
                        size={28}
                        isHumanVerified={isHumanVerified}
                        isDisabled={senderIsDisabled}
                      />
                    )}
                  </div>
                  <div className="max-w-[75%]">
                    {showHeader && (
                      <div className={`text-[11px] text-gray-500 mb-0.5 ${isSelf ? 'text-right' : 'text-left'}`}>
                        {!isSelf && senderIsDisabled ? <DisabledUserName /> : senderName} •{' '}
                        {format(new Date(mp.createdAt), 'MMM d, h:mm a')}
                      </div>
                    )}
                    {mp.attachments?.length > 0 && <MessagePartImages attachments={mp.attachments} isSelf={isSelf} />}
                    {mp.content?.trim() && (
                      <TranslatableMessageText
                        content={mp.content}
                        sourceLocale={mp.sourceLocale}
                        translations={mp.translations}
                        isSelf={isSelf}
                        messagePartId={mp.id}
                        threadId={threadId}
                      />
                    )}
                  </div>
                </div>
              </React.Fragment>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      {isInApp && (
        <div className="flex-shrink-0 border-t border-gray-200 bg-white px-3 py-2">
          {awaitingReply && reply.trim().length === 0 && attachments.length === 0 && (
            <DmQuickReplies phrases={quickPhrases} onSend={sendQuickReply} disabled={sending || uploading} />
          )}
          <form ref={dropScope} onSubmit={handleSend} className="relative">
            <Textarea
              ref={replyRef}
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={(e) => {
                const files = imagesFromClipboard(e);
                if (files.length) {
                  e.preventDefault();
                  addFiles(files);
                }
              }}
              placeholder={t('messages.reply-placeholder')}
              rows={2}
              maxLength={MAX_CONTENT_LENGTH}
              className="resize-none text-sm w-full"
              aria-label={t('messages.reply-placeholder')}
            />
            <div className="flex items-center justify-between mt-1">
              <div className="flex items-center gap-1">
                <Popover open={emojiPickerOpen} onOpenChange={setEmojiPickerOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      aria-label={t('dm.insert-emoji', 'Insert emoji')}
                      className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                    >
                      <Smile className="size-4" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 w-72" align="start" side="top" sideOffset={6}>
                    <EmojiPickerPopover
                      textareaRef={replyRef}
                      value={reply}
                      onChange={setReply}
                      onClose={() => setEmojiPickerOpen(false)}
                    />
                  </PopoverContent>
                </Popover>
                <MediaDropPill
                  {...dropProps}
                  label={t('dm.attachments.add', 'Attach a file')}
                  chipsPlacement="above"
                  dropScope={dropScope}
                />
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={`text-[11px] ${reply.trim().length > MAX_CONTENT_LENGTH ? 'text-red-600' : 'text-gray-500'}`}
                >
                  {reply.trim().length} / {MAX_CONTENT_LENGTH}
                </span>
                <Button type="submit" disabled={!canSend} aria-label={t('messages.send')} size="sm">
                  <SendHorizontal className="size-4" />
                </Button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
